import { useRef, useState } from 'react';
import { api, ApiFailure, type UploadResult } from '../api';
import { Notice } from './Panels';

const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm';

export function Upload({ onLoaded }: { onLoaded: (result: UploadResult) => void }) {
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const input = useRef<HTMLInputElement>(null);

  async function send(file: File): Promise<void> {
    setFailure(null);
    setProgress(0);
    try {
      const result = await api.upload(file, setProgress);
      onLoaded(result);
    } catch (error) {
      // The API's message and remedy are shown verbatim — it knows why the
      // upload failed and what to do about it, and the client does not.
      setFailure(
        error instanceof ApiFailure
          ? error
          : new ApiFailure('The upload failed.', 'Try again.', 0),
      );
    } finally {
      setProgress(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {failure !== null && (
        <Notice tone="error" message={failure.message} remedy={failure.remedy} />
      )}

      <div
        className={over ? 'dropzone over' : 'dropzone'}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const file = event.dataTransfer.files[0];
          if (file !== undefined) void send(file);
        }}
      >
        <h1>Drop a spreadsheet to analyse it</h1>
        <p>
          CSV, TSV or Excel. Nothing is analysed in the browser — the file is parsed once, on the
          server, so every figure you see comes from the same profile.
        </p>

        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void send(file);
            event.target.value = '';
          }}
        />

        <button className="primary" onClick={() => input.current?.click()} disabled={progress !== null}>
          {progress === null ? 'Choose a file' : 'Uploading…'}
        </button>

        {progress !== null && (
          <div className="progress" aria-label="Upload progress">
            <div style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}

        <p className="muted" style={{ marginTop: 18, marginBottom: 0 }}>
          Accepted: {ACCEPT.split(',').join(' · ')}
        </p>
      </div>
    </div>
  );
}
